import { useCallback, useEffect, useState } from "react";
import type { NotifiableError } from "@bugsnag/js";
import { bugsnagClient } from "bugsnag";
import type { CameraPosition } from "components/features/PanoramaViewer/types";
import api from "utils/api";
import { isMappedFieldKey } from "./utils";
import { FIELD_TO_PAYLOAD_KEY } from "./constants";
import { EditableField, UseEditFrameStateArgs } from "./types";

export function useEditFrameState<FrameField extends EditableField>({
  currentFrame,
  fields,
  refetchDocumentation,
}: UseEditFrameStateArgs<FrameField>) {
  const [isEditingFrameState, setIsEditingFrameState] = useState(false);
  const [isUpdatingFrameState, setIsUpdatingFrameState] = useState(false);

  const [editedFrameState, setEditedFrameState] =
    useState<CameraPosition>(currentFrame);

  const resetEditedFrameState = useCallback(() => {
    setEditedFrameState(currentFrame);
  }, [currentFrame]);

  const frameState = isEditingFrameState ? editedFrameState : currentFrame;

  const setEditedFrameFields = useCallback(
    <K extends Partial<CameraPosition>>(fields: K) => {
      setEditedFrameState((prev) => ({ ...prev, ...fields }));
    },
    []
  );

  const startEditingFrameState = useCallback(() => {
    setIsEditingFrameState(true);
    resetEditedFrameState();
  }, [resetEditedFrameState]);

  const cancelEditingFrameState = useCallback(() => {
    setIsEditingFrameState(false);
    resetEditedFrameState();
  }, [resetEditedFrameState]);

  const updateFrameState = useCallback(async () => {
    setIsUpdatingFrameState(true);

    try {
      const patch = fields.reduce<
        Partial<Record<FrameField | "translation" | "opticalCenter", unknown>>
      >((acc, fieldKey) => {
        const nextValue = editedFrameState[fieldKey];
        const currentValue = currentFrame[fieldKey];

        if (nextValue === currentValue) return acc;

        const payloadKey = isMappedFieldKey(fieldKey)
          ? FIELD_TO_PAYLOAD_KEY[fieldKey]
          : fieldKey;

        acc[payloadKey] = nextValue;

        return acc;
      }, {});

      if (Object.keys(patch).length === 0) {
        setIsEditingFrameState(false);
        return;
      }

      await api.patchDocumentationFrame(currentFrame._id, patch);

      setIsEditingFrameState(false);
      await refetchDocumentation();
    } catch (error) {
      bugsnagClient.notify(error as NotifiableError);
      console.error("Error updating frame state:", error);
    } finally {
      setIsUpdatingFrameState(false);
    }
  }, [fields, editedFrameState, currentFrame, refetchDocumentation]);

  useEffect(() => {
    if (!isEditingFrameState) resetEditedFrameState();
  }, [isEditingFrameState, resetEditedFrameState]);

  return {
    isEditingFrameState,
    isUpdatingFrameState,

    editedFrameState,
    setEditedFrameState,
    setEditedFrameFields,

    frameState,

    startEditingFrameState,
    cancelEditingFrameState,
    updateFrameState,
  };
}
